// based on https://github.com/antonioru/beautiful-react-hooks/blob/master/src/useMediaQuery.ts
import { hasWindow } from "@omni/utils/dom.utils";
import { useEffect, useState } from "react";

const isAPISupported = (api: string): boolean =>
    hasWindow() ? api in window : false;
const isClient = typeof window === "object";

/**
 * Accepts a media query string then uses the
 * [window.matchMedia](https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia) API to determine if it
 * matches with the current document.<br />
 * It also monitor the document changes to detect when it matches or stops matching the media query.<br />
 * Returns the validity state of the given media query.
 *
 */
const useMediaQuery = (mediaQuery: string): boolean | null => {
    const [isVerified, setIsVerified] = useState(
        isClient ? !!window?.matchMedia(mediaQuery).matches : false
    );

    useEffect(() => {
        const mediaQueryList = window.matchMedia(mediaQuery);
        const documentChangeHandler = () =>
            setIsVerified(!!mediaQueryList.matches);

        try {
            mediaQueryList.addEventListener("change", documentChangeHandler);
        } catch {
            // Safari isn't supporting mediaQueryList.addEventListener
            mediaQueryList.addListener(documentChangeHandler);
        }

        documentChangeHandler();
        return () => {
            try {
                mediaQueryList.removeEventListener(
                    "change",
                    documentChangeHandler
                );
            } catch {
                // Safari isn't supporting mediaQueryList.removeEventListener
                mediaQueryList.removeListener(documentChangeHandler);
            }
        };
    }, [mediaQuery]);

    if (!isClient || !isAPISupported("matchMedia")) {
        return null;
    }

    return isVerified;
};

export default useMediaQuery;
